import type {
  FollowupChannel,
  FollowupUnit,
  MessageChannel,
  MessageTemplate,
  MessagerieSettings,
  OutboxMessage,
  OutboxStatus,
  OutboxSummary,
  ServiceFollowup,
  TemplatePreview,
} from "@shared/modules/x-messagerie";
import { api } from "../../../core/lib/api";
import { moduleEnabled } from "../../../core/blueprint";

// Fonctions typées par route de /api/messagerie. Les formes d'entrée sont
// redites ici (zod n'est pas une dépendance du web).

const BASE = "/api/messagerie";

const json = (method: "POST" | "PATCH" | "PUT", body: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}

// ---- Réglages et modèles (admin) ----

export const getSettings = () => api<MessagerieSettings>(`${BASE}/settings`);
export const updateSettings = (input: Partial<MessagerieSettings>) =>
  api<MessagerieSettings>(`${BASE}/settings`, json("PUT", input));
/** Courriel de test vers une adresse : vérifie expéditeur, gabarit et clés (admin). */
export const sendTestEmail = (to: string) =>
  api<OutboxMessage>(`${BASE}/outbox/test-email`, json("POST", { to }));

export interface TemplateInput {
  channel?: MessageChannel;
  name?: string;
  subject?: string | null;
  body?: string;
  enabled?: boolean;
}

export const listTemplates = () => api<MessageTemplate[]>(`${BASE}/templates`);
export const updateTemplate = (id: string, input: TemplateInput) =>
  api<MessageTemplate>(`${BASE}/templates/${id}`, json("PATCH", input));
export const previewTemplate = (id: string, appointmentId?: string) =>
  api<TemplatePreview>(
    `${BASE}/templates/${id}/preview`,
    json("POST", appointmentId ? { appointmentId } : {}),
  );
/** Envoi d'essai du modèle rendu, à un cellulaire ou un courriel, tout de suite. */
export const sendTemplateTest = (id: string, to: string, appointmentId?: string) =>
  api<OutboxMessage>(
    `${BASE}/templates/${id}/test`,
    json("POST", { to, ...(appointmentId ? { appointmentId } : {}) }),
  );

// ---- Rappels de suivi par service (admin) ----

export interface FollowupInput {
  serviceId: string;
  label: string;
  delayValue: number;
  delayUnit: FollowupUnit;
  repeatCount?: number;
  channel?: FollowupChannel;
  subject?: string | null;
  body: string;
  enabled?: boolean;
}

export const listFollowups = (serviceId?: string) =>
  api<ServiceFollowup[]>(`${BASE}/followups${qs({ serviceId })}`);
export const createFollowup = (input: FollowupInput) =>
  api<ServiceFollowup>(`${BASE}/followups`, json("POST", input));
export const updateFollowup = (id: string, input: Partial<FollowupInput>) =>
  api<ServiceFollowup>(`${BASE}/followups/${id}`, json("PATCH", input));
export const deleteFollowup = (id: string) =>
  api<void>(`${BASE}/followups/${id}`, { method: "DELETE" });
export const previewFollowup = (input: {
  channel: FollowupChannel;
  subject?: string | null;
  body: string;
}) => api<TemplatePreview>(`${BASE}/followups/preview`, json("POST", input));

// ---- Boîte d'envoi ----

export interface OutboxQuery {
  status?: OutboxStatus;
  channel?: MessageChannel;
  q?: string;
  limit?: number;
}

export interface MessageInput {
  channel: MessageChannel;
  to?: string;
  clientId?: string;
  subject?: string;
  body: string;
}

export const listOutbox = (query: OutboxQuery = {}) =>
  api<OutboxMessage[]>(
    `${BASE}/outbox${qs({ status: query.status, channel: query.channel, q: query.q, limit: query.limit })}`,
  );
export const getSummary = () => api<OutboxSummary>(`${BASE}/outbox/summary`);
export const createMessage = (input: MessageInput) =>
  api<OutboxMessage>(`${BASE}/outbox`, json("POST", input));
export const cancelMessage = (id: string) =>
  api<OutboxMessage>(`${BASE}/outbox/${id}/cancel`, { method: "POST" });
export const retryMessage = (id: string) =>
  api<OutboxMessage>(`${BASE}/outbox/${id}/retry`, { method: "POST" });

// ---- Atelier : contrat HTTP seulement (aucun import du module), si actif ----

export const atelierEnabled = moduleEnabled("x-atelier");

export interface ClientHit {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
}

export interface AppointmentHit {
  id: string;
  title: string;
  clientName: string;
  startAt: number | string;
  status: string;
}

export const searchClients = (q: string) =>
  atelierEnabled
    ? api<ClientHit[]>(`/api/atelier/clients${qs({ q })}`)
    : Promise.resolve([] as ClientHit[]);

/** Rendez-vous vivants (ni annulés ni récupérés) pour l'aperçu des modèles. */
export const listAppointmentsForPreview = () =>
  atelierEnabled
    ? api<AppointmentHit[]>(
        `/api/atelier/appointments${qs({ status: "reserve,confirme,vehicule_recu,en_cours,termine,pret", limit: 60 })}`,
      )
    : Promise.resolve([] as AppointmentHit[]);
